import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/api';
import PasswordChangeModal from '../components/PasswordChangeModal';
import i18n from '../i18n';

const UserProfile = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    email: currentUser?.email || '',
    full_name: currentUser?.full_name || '',
    preferred_language: currentUser?.preferred_language || 'ca'
  });
  const [loading, setLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If language preference changes, update the UI language immediately
    if (name === 'preferred_language') {
      i18n.changeLanguage(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userService.updateUser(currentUser.id, formData);
      // Refresh the page to update currentUser in context
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(t('userProfile.updateError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (userId, passwordData) => {
    try {
      await userService.changePassword(userId, passwordData);
      toast.success(t('userManagement.passwordChangeSuccess'));
      setShowPasswordChange(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(t('userManagement.passwordChangeError'));
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">{t('userProfile.notLoggedIn')}</h2>
          <p className="text-gray-600">{t('userProfile.pleaseLogin')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-3xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-semibold text-gray-900">{t('userProfile.title')}</h1>
            <p className="text-sm text-gray-600">{t('userProfile.subtitle')}</p>
          </div>

          <div className="p-6">
            {/* Profile Information */}
            <div className="mb-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('userProfile.profileInformation')}</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.username')}
                    </label>
                    <input
                      type="text"
                      value={currentUser.username}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('userProfile.usernameCannotChange')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.email')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.fullName')}
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.organization')}
                    </label>
                    <input
                      type="text"
                      value={currentUser.organization}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('userProfile.organizationManagedByAdmin')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('auth.role')}
                    </label>
                    <input
                      type="text"
                      value={currentUser.role}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 capitalize"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('userProfile.roleManagedByAdmin')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userProfile.preferredLanguage')}
                    </label>
                    <select
                      name="preferred_language"
                      value={formData.preferred_language}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-accent-500 focus:border-accent-500"
                    >
                      <option value="ca">Català</option>
                      <option value="es">Español</option>
                      <option value="en">English</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">{t('userProfile.languageDescription')}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('userProfile.accountStatus')}
                    </label>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      currentUser.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {currentUser.is_active ? t('userManagement.isActive') : t('userManagement.isInactive')}
                    </span>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50"
                  >
                    {loading ? t('userProfile.updating') : t('userProfile.updateProfile')}
                  </button>
                </div>
              </form>
            </div>

            {/* Security Section */}
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('userProfile.security')}</h2>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{t('auth.password')}</h3>
                    <p className="text-sm text-gray-600">
                      {currentUser.last_password_change 
                        ? t('userProfile.passwordLastChanged', { date: new Date(currentUser.last_password_change).toLocaleDateString() })
                        : t('userProfile.passwordNeverChanged')
                      }
                    </p>
                  </div>
                  <button
                    onClick={() => setShowPasswordChange(true)}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    {t('userProfile.changePassword')}
                  </button>
                </div>
              </div>

              {currentUser.must_change_password && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">
                        {t('userProfile.passwordChangeRequired')}
                      </h3>
                      <p className="text-sm text-yellow-700">
                        {t('userProfile.passwordChangeRequiredMessage')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Account Information */}
            <div className="border-t border-gray-200 pt-8">
              <h2 className="text-lg font-medium text-gray-900 mb-4">{t('userProfile.accountInformation')}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">{t('userProfile.accountCreated')}</span>
                  <span className="ml-2 text-gray-600">
                    {new Date(currentUser.created_at).toLocaleDateString()}
                  </span>
                </div>
                
                <div>
                  <span className="font-medium text-gray-700">{t('userProfile.lastUpdated')}</span>
                  <span className="ml-2 text-gray-600">
                    {currentUser.updated_at 
                      ? new Date(currentUser.updated_at).toLocaleDateString()
                      : t('common.never')
                    }
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Change Modal */}
      {showPasswordChange && (
        <PasswordChangeModal
          user={currentUser}
          onClose={() => setShowPasswordChange(false)}
          onSubmit={handlePasswordChange}
          isAdminReset={false}
        />
      )}
    </div>
  );
};

export default UserProfile;